/* eslint-disable react-hooks/rules-of-hooks,react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import {
  func,
  shape,
  string,
  bool
} from 'prop-types';
import {
  Loading as LoadingPage,
  CompositeBanner,
  ArticleSection,
  ModulabIconNames,
  CtaBanner,
  sizes,
  SpacingContainer,
  MODULAB_COLORS,
  WithFixedBannerLayout,
} from '@hyphengroup/cag-components';
import ErrorPage from 'pages/ErrorPage';
import {
  contentGroupingsTrack,
  subscribeBtnTracking,
  withTracking,
} from 'components/tracking';
import withApplicationConfig from 'containers/withApplicationConfig';
import Layout from 'components/layout';
import {
  getStaticText,
  getImages,
} from 'helpers/Utils';
import { compose } from 'react-apollo';
import usePageReloadOnHistoryNavigation from 'hooks/usePageReloadOnHistoryNavigation';
import {
  withAddNewsletterSubscription,
  withLandingPage,
} from 'containers/withQueries';

const EmailLanding = ({
  landingPageQuery,
  addNewsletterSubscription,
  localisation,
  language,
  track,
  showStickyBanner,
  hideEmailInputOnSuccessSubscription
}) => {
  const [pageLoading] = usePageReloadOnHistoryNavigation();
  const [email, setEmail] = useState('');
  const newsLetterLang = localisation[language];
  const [bannerHeight, setBannerHeight] = useState();
  const bannerRef = useRef(null);
  const [showEmailInput, setShowEmailInput] = useState(true)
  const {
    pageConfig,
    customerProfile,
    componentConfig,
    seoMetadata,
    landingPage,
    refetch,
    error,
    loading,
  } = landingPageQuery;
  const staticTextList = getStaticText(landingPageQuery);
  const images = getImages(landingPageQuery);

  if (!landingPageQuery || loading || pageLoading) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <Layout
        seoMetadata={seoMetadata}
        customerProfile={customerProfile}
        componentConfig={componentConfig}
        staticText={staticTextList}
        refetch={refetch}
        images={images}
        {...pageConfig}
      >
        <ErrorPage errorCode={landingPageQuery.error} />
      </Layout>
    );
  }

  useEffect(() => {
    if (bannerRef?.current) {
      const { height } = bannerRef.current.getBoundingClientRect();
      setBannerHeight(height);
    }
  }, [bannerRef]);

  const categories = landingPage.categories.map(c => c.subCategories);
  const { ctaButton, banner } = landingPage;
  const heroImages = {
    desktop: banner.desktopImage.url,
    mobile: banner.mobileImage.url,
  };
  const heroBannerTnc = componentConfig
    .filter(item => item.title === 'WEEKLY_EMAIL_PAGE_HERO_BANNER_TNC')
    .flatMap(item => item.richText)
    .map(obj => obj.values.text)[0] ?? '';

  contentGroupingsTrack(track, 'static');

  const renderCompositeBanner = () => {
    const emailInput = {
      value: email,
      placeholder: staticTextList.WEEKLY_EMAIL_PAGE_SUBSCRIBE_INPUT_PLACEHOLDER,
      onChange: ({ value }) => {
        setEmail(value);
      },
      onSubmit: () => {
        setShowEmailInput(false)
        subscribeBtnTracking(track, 'static', 'hero-banner');
        return addNewsletterSubscription({
          variables: {
            email,
            language: newsLetterLang,
            skipWelcome: false,
            source: 'WEB',
          },
        }).then(() => true);
      },
      submitButtonText: staticTextList.WEEKLY_EMAIL_PAGE_SUBSCRIBE_BTN_TEXT,
      errorMessage: staticTextList.WEEKLY_EMAIL_PAGE_SUBSCRIBE_FAIL_MESSAGE,
      withoutFieldLabel: true,
      showValidationMessageOnBlur: true,
      color: MODULAB_COLORS.BASE_PRIMARY,
    };

    return (
      <CompositeBanner
        heroImages={heroImages}
        emailInput={showEmailInput ? emailInput : hideEmailInputOnSuccessSubscription ? null: emailInput}
        dataSelector="weekly-email-composite-banner"
        successMessage={staticTextList.WEEKLY_EMAIL_PAGE_SUBSCRIBE_SUCCESS_MESSAGE}
        terms={heroBannerTnc}
      />
    );
  };

  const renderArticles = categories.map((article) => {
    const cta = {
      title: article.viewMoreCta.title,
      url: article.viewMoreCta.href,
      icon: {
        iconName: ModulabIconNames.ARROW_RIGHT,
        iconSize: 3,
        iconColor: MODULAB_COLORS.PRIMARY_DEFAULT,
      },
    };
    const cardContent = article.blogArticles.map(({ title, link, image }) => ({
      title,
      url: link,
      image: image?.url,
    }));
    const uniqueKey = `${article.title}-article-section`;

    return (
      <SpacingContainer
        key={uniqueKey}
        margin={[sizes(2), sizes(2), sizes(2), `${sizes(4)} ${sizes(4)} ${sizes(4)} ${sizes(17)}`]}
        width="unset"
      >
        <ArticleSection
          heading={article.heading}
          subHeading={article.subheading}
          readMore={cta}
          articles={cardContent}
          dataSelector={uniqueKey}
        />
      </SpacingContainer>
    );
  });
  const renderCtaBanner = () => (
    <CtaBanner
      title={staticTextList.WEEKLY_EMAIL_PAGE_CTA_BANNER_TITLE}
      subtitle={staticTextList.WEEKLY_EMAIL_PAGE_CTA_BANNER_SUBTITLE}
      ctaButton={{
        text: [ctaButton.title, ctaButton.title],
        onClick: () => {
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        },
      }}
    />
  );
        return (
            <Layout
                style={{paddingBottom: bannerHeight && `${bannerHeight}px`}}
                seoMetadata={seoMetadata}
                customerProfile={customerProfile}
                componentConfig={componentConfig}
                staticText={staticTextList}
                refetch={refetch}
                images={images}
                {...pageConfig}
            >
                <WithFixedBannerLayout
                    bannerRef={bannerRef}
                    banner={showStickyBanner ? renderCtaBanner() : null}
                >
                    {renderCompositeBanner()}
                    {renderArticles}
                </WithFixedBannerLayout>
            </Layout>
        );
};

EmailLanding.propTypes = {
  track: func.isRequired,
  localisation: shape({}).isRequired,
  language: string.isRequired,
  contentstackKeys: shape({}),
  showStickyBanner: bool.isRequired,
  hideEmailInputOnSuccessSubscription: bool.isRequired
};

export default withApplicationConfig(
  compose(withTracking, withLandingPage('weeklyEmailLandingPage', 'Weekly Email'), withAddNewsletterSubscription)(EmailLanding), ['languages', 'socialMedia', 'frontPage', 'contentstackKeys', 'stickyBannerFooter']
);
